import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Search, 
  Download, 
  Filter, 
  MoreVertical, 
  Settings, 
  Ban, 
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Star,
  User
} from 'lucide-react';

interface BetaRequest {
  id: string;
  user_id: string;
  program: string;
  status: 'pending' | 'approved' | 'testing';
  invite_code?: string;
  created_at: string;
  user: {
    display_name: string;
    email: string;
    energy_type: string;
  };
}

export function BetaManagement() {
  const [requests, setRequests] = useState<BetaRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'testing'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    const loadRequests = async () => {
      try {
        const { data, error } = await supabase
          .from('beta_access')
          .select(`
            *,
            user:profiles!beta_access_user_id_fkey (
              display_name,
              email,
              energy_type
            )
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setRequests(data || []);
      } catch (error) {
        console.error('Error loading beta requests:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger les demandes d'accès bêta.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadRequests();

    // Subscribe to real-time updates
    const subscription = supabase
      .channel('beta_requests')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'beta_access' },
        () => loadRequests()
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [toast]);

  const handleApprove = async (requestId: string) => {
    try {
      const inviteCode = `PW-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      
      const { error } = await supabase
        .from('beta_access')
        .update({
          status: 'approved',
          invite_code: inviteCode
        })
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: "Accès approuvé",
        description: "L'utilisateur a été approuvé pour le programme bêta.",
      });

      // Refresh the list
      loadRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'approuver la demande.",
      });
    }
  };

  const handleReject = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('beta_access')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: "Demande rejetée",
        description: "La demande d'accès a été rejetée.",
      });

      // Refresh the list
      loadRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de rejeter la demande.",
      });
    }
  };

  const filteredRequests = requests
    .filter(request => filter === 'all' || request.status === filter)
    .filter(request => 
      searchTerm === '' || 
      request.user.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-orange-600';
      case 'approved': return 'text-green-600';
      case 'testing': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'approved': return 'Approuvé';
      case 'testing': return 'En test';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold">Gestion des bêta-testeurs</h2>
            <p className="text-gray-500">
              {requests.filter(r => r.status === 'pending').length} demande(s) en attente
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="border rounded-md px-3 py-2"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="approved">Approuvés</option>
                <option value="testing">En test</option>
              </select>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {filteredRequests.length === 0 ? (
            <p className="text-center text-gray-500 py-4">
              Aucune demande d'accès bêta trouvée.
            </p>
          ) : (
            filteredRequests.map((request) => (
              <Card key={request.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{request.user.display_name}</span>
                        <span className="text-sm text-gray-500">{request.user.email}</span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-purple-500" />
                          {request.user.energy_type}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-gray-500" />
                          {format(new Date(request.created_at), 'dd MMMM yyyy', { locale: fr })}
                        </span>
                      </div>

                      <div className={`flex items-center gap-2 ${getStatusColor(request.status)}`}>
                        {request.status === 'pending' ? (
                          <Clock className="w-4 h-4" />
                        ) : request.status === 'approved' ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <Star className="w-4 h-4" />
                        )}
                        <span>{getStatusText(request.status)}</span>
                        {request.invite_code && (
                          <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                            Code: {request.invite_code}
                          </span>
                        )}
                      </div>
                    </div>

                    {request.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApprove(request.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => handleReject(request.id)}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}